// The Helios motion tokens a component animates from TypeScript: the
// `$duration-carousel` and `$ease-carousel` tokens in tokens/_spacing.scss.
// CSS transitions read the tokens; the Carousel moves its slides frame by
// frame (a slide can leave one edge and re-enter at the other mid-move, which
// a CSS transition cannot draw), so it reads these. The Carousel test checks
// both against Sass, as tokens.test.ts does for breakpoints.ts.

/** `$duration-carousel`: one Carousel move, in milliseconds. */
export const CAROUSEL_DURATION_MS = 900

/** `$ease-carousel`: the control points of its `cubic-bezier()`. */
export const CAROUSEL_EASE = [0.65, 0, 0.35, 1] as const

/**
 * The `cubic-bezier(x1, y1, x2, y2)` timing function as a function of time:
 * progress in [0, 1] → eased progress, as a browser draws it.
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  const coordinate = (t: number, p1: number, p2: number) => {
    const inverse = 1 - t
    return 3 * inverse * inverse * t * p1 + 3 * inverse * t * t * p2 + t * t * t
  }
  const slope = (t: number, p1: number, p2: number) =>
    3 * (1 - t) * (1 - t) * p1 +
    6 * (1 - t) * t * (p2 - p1) +
    3 * t * t * (1 - p2)

  return (progress: number) => {
    if (progress <= 0) return 0
    if (progress >= 1) return 1
    // Solve x(t) = progress for t: Newton's method, with bisection when the
    // slope is flat, then read y(t).
    let t = progress
    for (let iteration = 0; iteration < 8; iteration += 1) {
      const error = coordinate(t, x1, x2) - progress
      if (Math.abs(error) < 1e-7) break
      const derivative = slope(t, x1, x2)
      if (Math.abs(derivative) < 1e-6) break
      t -= error / derivative
    }
    if (t < 0 || t > 1 || Math.abs(coordinate(t, x1, x2) - progress) > 1e-7) {
      let low = 0
      let high = 1
      t = progress
      for (let iteration = 0; iteration < 40; iteration += 1) {
        const x = coordinate(t, x1, x2)
        if (Math.abs(x - progress) < 1e-7) break
        if (x < progress) low = t
        else high = t
        t = (low + high) / 2
      }
    }
    return coordinate(t, y1, y2)
  }
}

/** `$ease-carousel` as a function: progress in [0, 1] → eased progress. */
export const carouselEase = cubicBezier(...CAROUSEL_EASE)
